export function getReviewCarouselPageCount(cardCount, cardsPerPage = 4) {
  return Math.max(1, Math.ceil(cardCount / cardsPerPage));
}

export function getReviewCarouselPageScrollLeft(cardScrollLeft, maxScrollLeft) {
  return Math.min(Math.max(0, cardScrollLeft), Math.max(0, maxScrollLeft));
}

class CustomerReviewsCarousel {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-customer-reviews-track]');
    this.cards = Array.from(section.querySelectorAll('[data-customer-reviews-card]'));
    this.dots = Array.from(section.querySelectorAll('[data-customer-reviews-dot]'));
    this.cardsPerPage = 4;
    this.isPointerDragging = false;
    this.pointerStartX = 0;
    this.pointerStartScrollLeft = 0;
    this.requestedPage = null;

    if (!this.track || this.cards.length <= this.cardsPerPage || !this.dots.length) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        const page = Number(dot.dataset.customerReviewsDot);
        this.requestedPage = page;
        this.setActivePage(page);
        window.requestAnimationFrame(() => this.scrollToPage(page));
      });
    });

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerUp);
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.updateDots();
  }

  get isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  scrollToPage(page) {
    const card = this.cards[page * this.cardsPerPage];
    if (!card) return;

    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
    const targetScrollLeft = getReviewCarouselPageScrollLeft(this.getCardScrollLeft(card), maxScrollLeft);

    this.track.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  }

  getCardScrollLeft(card) {
    const trackBounds = this.track.getBoundingClientRect();
    const cardBounds = card.getBoundingClientRect();

    return cardBounds.left - trackBounds.left + this.track.scrollLeft;
  }

  handlePointerDown(event) {
    this.requestedPage = null;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.isPointerDragging = true;
    this.pointerStartX = event.clientX;
    this.pointerStartScrollLeft = this.track.scrollLeft;
    this.track.classList.add('is-dragging');
    this.track.setPointerCapture(event.pointerId);
  }

  handlePointerMove(event) {
    if (!this.isPointerDragging) return;

    event.preventDefault();
    this.track.scrollLeft = this.pointerStartScrollLeft - (event.clientX - this.pointerStartX);
  }

  handlePointerUp(event) {
    if (!this.isPointerDragging) return;

    this.isPointerDragging = false;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
  }

  handleScroll() {
    window.requestAnimationFrame(() => this.updateDots());
  }

  handleResize() {
    this.updateDots();
  }

  updateDots() {
    if (!this.isDesktop) return;

    const trackBounds = this.track.getBoundingClientRect();
    const trackScrollLeft = this.track.scrollLeft;
    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
    const pageCount = getReviewCarouselPageCount(this.cards.length, this.cardsPerPage);
    const pagePositions = Array.from({ length: pageCount }, (_, page) => {
      const card = this.cards[page * this.cardsPerPage];
      const cardScrollLeft = card.getBoundingClientRect().left - trackBounds.left + trackScrollLeft;
      return getReviewCarouselPageScrollLeft(cardScrollLeft, maxScrollLeft);
    });
    const activePage = pagePositions.reduce(
      (closestPage, pagePosition, page) => {
        const currentDistance = Math.abs(pagePosition - trackScrollLeft);
        const closestDistance = Math.abs(pagePositions[closestPage] - trackScrollLeft);

        return currentDistance < closestDistance ? page : closestPage;
      },
      0,
    );

    if (this.requestedPage !== null) {
      const requestedPosition = pagePositions[this.requestedPage];
      if (Math.abs(requestedPosition - trackScrollLeft) <= 1) {
        this.requestedPage = null;
      } else {
        this.setActivePage(this.requestedPage);
        return;
      }
    }

    this.setActivePage(activePage);
  }

  setActivePage(activePage) {
    this.dots.forEach((dot, index) => {
      const isActive = index === activePage;
      dot.setAttribute('aria-selected', String(isActive));
    });
  }
}

function initCustomerReviewsCarousels(root = document) {
  const sections = [
    ...(root.matches?.('[data-customer-reviews]') ? [root] : []),
    ...root.querySelectorAll('[data-customer-reviews]'),
  ];

  sections.forEach((section) => {
    if (section.dataset.customerReviewsReady) return;

    section.dataset.customerReviewsReady = 'true';
    new CustomerReviewsCarousel(section);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCustomerReviewsCarousels());
  } else {
    initCustomerReviewsCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => initCustomerReviewsCarousels(event.target));
}
